import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { from, iif, mergeMap, of, switchMap } from 'rxjs'
import { useObservable } from 'rxjs-hooks'

import {
  BusinessRounded,
  DocumentScannerRounded,
  ErrorOutlineRounded,
  ExpandCircleDownOutlined,
  FileOpenRounded,
  GroupsRounded,
  LanguageRounded,
  LogoutRounded,
  OpenInNewRounded,
} from '@mui/icons-material'
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material'
import {
  getRandomColor,
  LoginTypes,
  PreferredLanguageType,
  StorageKeys,
  stringToBoolean,
  useAuthService,
  useAuthStore,
  useSubjectSelector,
  useSubscriptionRef,
} from '@procom-labs/common'
import { useAlert } from '@procom-labs/molecules'

import { RoundLoadingButton } from '@resume-portal/components/ui/round-button'
import { environment } from '@resume-portal/environment'
import { useVerifyEmail } from '@resume-portal/features/resume-builder/hooks'
import { atsCredStorage } from '@resume-portal/lib'
import {
  AccessTypes,
  BHNewTabMessageType,
  DiscoverabilityTypes,
  IDiscoveredOrganization,
  PricingPlans,
  ResumeBuilderStateEnum,
  ServicePlanChargeTypes,
  UserOrganizationStatuses,
  UserStatuses,
} from '@resume-portal/models'
import { CommonRoutesPaths } from '@resume-portal/routes/routes.enum'
import {
  resumeBuilderStore,
  servicePlanStore,
  userOrganizationStore,
} from '@resume-portal/stores'

import {
  resumeParserService,
  resumeTemplateService,
  servicePlanService,
  userOrganizationService,
} from '../../services'
import { ReportProblemDialog } from '../report-problem-dialog'
import { Avatar } from '../ui/avatar'
import { UsageStatus } from './usage-status'

const MenuItemStyles: SxProps = {
  color: 'gorillaResume.button',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

interface LocationState {
  firstLogin?: boolean
  emailVerified?: boolean
}

const LanguageToggle: React.FC<{ isDarkMode?: boolean }> = ({
  isDarkMode = false,
}) => {
  const { t, i18n } = useTranslation('main')
  const activeColor = isDarkMode ? 'common.white' : 'gorillaResume.button'
  return (
    <>
      <LanguageRounded />
      <Typography
        variant="body2Bold"
        color={isDarkMode ? '#7c86ad' : 'gorillaResume.text.secondary'}
      >
        <Box
          display="inline"
          color={
            i18n.language === PreferredLanguageType.EN ? activeColor : 'inherit'
          }
        >
          {t('profileMenu.en')}
        </Box>{' '}
        &#8226;{' '}
        <Box
          display="inline"
          color={
            i18n.language === PreferredLanguageType.FR ? activeColor : 'inherit'
          }
        >
          {t('profileMenu.fr')}
        </Box>
      </Typography>
    </>
  )
}

export const GorillaAppBar: React.FC<{ isAtsBullhorn?: boolean }> = ({
  isAtsBullhorn = false,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as LocationState
  const { addAlert } = useAlert()
  const { t, i18n } = useTranslation('main')
  const [searchParams] = useSearchParams()

  const authService = useAuthService()
  const authStore = useAuthStore()

  const currentUser = useObservable(() => authService.currentUser$)
  const atsUser = useSubjectSelector(authStore, ['currentUser']).currentUser
  const { state, taskId } = useSubjectSelector(resumeBuilderStore, [
    'state',
    'taskId',
  ])
  const {
    userOrganization,
    isPendingJoin,
    canAccessSettings,
    userOrgAccessType,
    hasServicePlan,
    servicePlan,
    teamManagementEnabled,
  } = useSubjectSelector(userOrganizationStore, [
    'userOrganization',
    'isPendingJoin',
    'canAccessSettings',
    'userOrgAccessType',
    'hasServicePlan',
    'servicePlan',
    'teamManagementEnabled',
  ])
  const { servicePlans } = useSubjectSelector(servicePlanStore, [
    'servicePlans',
  ])
  const subscriptionRef = useSubscriptionRef()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { handleSendVerificationEmail, isLoading } = useVerifyEmail()
  const [displayOpenNewTab, setDisplayOpenNewTab] = useState(false)
  const open = Boolean(anchorEl)
  const checkPendingSubscription = useSubscriptionRef()
  const languageSubscription = useSubscriptionRef()
  const remainingUsageSubscription = useSubscriptionRef()
  const templateSubscriptionRef = useSubscriptionRef()
  const servicePlansSubscription = useSubscriptionRef()
  const isDummyOrg = useMemo(
    () =>
      currentUser?.isEmailVerified &&
      userOrganization?.status === UserOrganizationStatuses.PendingActivation,
    [currentUser?.isEmailVerified, userOrganization?.status]
  )
  const [isReportProblemDialogOpen, setIsReportProblemDialogOpen] =
    useState(false)
  const avatarColor: string = useMemo(() => `#${getRandomColor()}`, [])
  const displayName = useMemo(() => {
    if (currentUser?.isEmailVerified) {
      if (currentUser?.firstName && currentUser.lastName) {
        return `${currentUser?.firstName} ${currentUser?.lastName}`
      }
      return ''
    }
    return !isAtsBullhorn && t('profileMenu.unverified')
  }, [
    currentUser?.firstName,
    currentUser?.isEmailVerified,
    currentUser?.lastName,
    isAtsBullhorn,
    t,
  ])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleOpenNewTabClick = (): void => {
    const candidateId = searchParams.get('EntityID')
    const entityType = searchParams.get('EntityType')
    const currentUrl = window.location.href
    const url = new URL(currentUrl)
    url.search = ''

    if (candidateId) url.searchParams.set('EntityID', candidateId)
    if (entityType) url.searchParams.set('EntityType', entityType)

    const newWindow = window.open(url.toString(), '_blank')
    const credentials = atsCredStorage.get()

    if (!newWindow) return

    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) return
      if (event.data === BHNewTabMessageType.Ready) {
        newWindow.postMessage(
          {
            [StorageKeys.Credentials]: credentials,
          },
          window.location.origin
        )
        window.removeEventListener('message', handleMessage)
      }
    }

    window.addEventListener('message', handleMessage)
  }

  const navigateToParse = useCallback(() => {
    if (
      taskId &&
      userOrganization?.id &&
      state !== ResumeBuilderStateEnum.Done &&
      !stringToBoolean(environment.ENABLE_RESUME_BUILDER_PARSER)
    )
      resumeParserService.cancelParse(taskId, userOrganization.id).subscribe()
    // Reset state to display initial file upload screen
    resumeBuilderStore.resetResumeState()
    if (isAtsBullhorn) return
    setAnchorEl(null)
    navigate(CommonRoutesPaths.Parse)
  }, [isAtsBullhorn, navigate, state, taskId, userOrganization?.id])

  const handleLanguageSwitch = useCallback(() => {
    const newLang =
      i18n.language === PreferredLanguageType.EN
        ? PreferredLanguageType.FR
        : PreferredLanguageType.EN
    if (isAtsBullhorn) {
      languageSubscription.current = from(
        i18n.changeLanguage(newLang)
      ).subscribe()
    } else {
      languageSubscription.current = authService
        .updateUserDetail(
          currentUser?.id ?? '',
          {
            ...currentUser,
            preferredLanguage: newLang,
          },
          true
        )
        .pipe(switchMap(() => i18n.changeLanguage(newLang)))
        .subscribe()
    }
  }, [isAtsBullhorn, currentUser, i18n, languageSubscription])

  const getFirstPublicOrganization = (
    organizations: IDiscoveredOrganization[]
  ): string => {
    return (
      organizations?.find(
        (discoveredOrg) =>
          discoveredOrg.discoverability === DiscoverabilityTypes.Public
      )?.id ?? ''
    )
  }

  const setServicePlan = useCallback(
    (servicePlanId?: string) => {
      if (!servicePlanId || !servicePlans.length) return
      const currServicePlan = servicePlans.find(
        (plan) => plan.id === servicePlanId
      )
      if (currServicePlan) {
        userOrganizationStore.dispatch({
          servicePlan: currServicePlan,
          teamManagementEnabled: currServicePlan.teamManagement,
          ...(currServicePlan.code !== PricingPlans.UnverifiedUser
            ? { hasServicePlan: true }
            : {}),
        })

        if (currServicePlan.chargeType === ServicePlanChargeTypes.UsageCount) {
          remainingUsageSubscription.current = userOrganizationService
            .getRemainingUsage(userOrganization?.id ?? '')
            .subscribe()
        }
      }
    },
    [servicePlans, userOrganization?.id]
  )

  useEffect(() => {
    if (currentUser?.email && !isAtsBullhorn) {
      subscriptionRef.current = userOrganizationService
        .getPrimaryUserOrganization()
        .pipe(
          mergeMap((org) =>
            iif(
              () => !org,
              currentUser?.isEmailVerified
                ? // The user is verified and doesn't have a dummy org -- they were invited
                  userOrganizationService.checkAndActivateUser().pipe(
                    switchMap((invitedOrg) =>
                      invitedOrg
                        ? of(invitedOrg)
                        : // Did not find an invited org, create a dummy one (edge case, shouldn't really happen)
                          userOrganizationService.createUserOrganization({
                            name: currentUser?.email || '',
                          })
                    )
                  )
                : // The user is not verified and doesn't have an org, we create a dummy one
                  userOrganizationService.createUserOrganization({
                    name: currentUser?.email || '',
                  }),
              org?.status === UserOrganizationStatuses.PendingActivation
                ? // User is verified and in a dummy org. Check if they were invited to an org
                  userOrganizationService
                    .checkAndActivateUser()
                    .pipe(
                      switchMap((invitedOrg) =>
                        invitedOrg ? of(invitedOrg) : of(org)
                      )
                    )
                : of(org)
            )
          ),
          switchMap((resOrg) => {
            // Find the user in the organization, if it exists
            const orgUser = resOrg?.users?.find(
              (user) => user.id === currentUser.id
            )

            // Dispatch the user's access type to the store
            if (orgUser?.accessType) {
              userOrganizationStore.dispatch({
                userOrgAccessType: orgUser.accessType,
              })
            }
            return of(resOrg)
          })
        )
        .subscribe()
    }
    if (!isAtsBullhorn) return
    subscriptionRef.current = userOrganizationService
      .getPrimaryUserOrganization()
      .pipe(
        mergeMap((org) =>
          iif(
            () => !org,
            // The user hasn't joined an organization yet, look for the first public org for their email domain
            userOrganizationService
              .getAvailableUserOrganizations()
              .pipe(
                switchMap((data) =>
                  userOrganizationService.joinOrganization(
                    getFirstPublicOrganization(data)
                  )
                )
              ),
            of(org)
          )
        )
      )
      .subscribe({
        next: (data) => {
          if (!data) {
            setAnchorEl(null)
            // There was no existing public organization for this user's email domain
            navigate(CommonRoutesPaths.AccessDenied)
          } else {
            userOrganizationStore.dispatch({
              userOrganization: data,
            })
          }
        },
      })
  }, [
    currentUser?.email,
    currentUser?.id,
    isAtsBullhorn,
    subscriptionRef,
    currentUser?.isEmailVerified,
  ])

  useEffect(() => {
    if (userOrganization?.id) {
      templateSubscriptionRef.current = resumeTemplateService
        .getTemplates(userOrganization.id)
        .subscribe()
    }
  }, [templateSubscriptionRef, userOrganization?.id])

  useEffect(() => {
    if (
      currentUser?.isEmailVerified &&
      userOrganization?.status === UserOrganizationStatuses.PendingActivation
    ) {
      // User is still on the dummy org, check if they have requested to join an org
      checkPendingSubscription.current = userOrganizationService
        .checkIfPendingOrganization()
        .subscribe()
    }
  }, [
    currentUser?.isEmailVerified,
    navigate,
    checkPendingSubscription,
    userOrganization?.status,
  ])

  useEffect(() => {
    if (servicePlans?.length) return
    servicePlansSubscription.current = servicePlanService
      .getServicePlans()
      .subscribe()
  }, [servicePlansSubscription, servicePlans?.length])

  useEffect(() => {
    if (!userOrganization || !servicePlans.length) return

    if (userOrganization?.servicePlanId) {
      setServicePlan(userOrganization.servicePlanId)
    } else if (userOrganization?.status === UserOrganizationStatuses.Active) {
      // As organization is active and it has no service plan, dispatch hasServicePlan as false
      userOrganizationStore.dispatch({
        hasServicePlan: false,
        servicePlan: null,
      })
    }
  }, [
    servicePlans.length,
    setServicePlan,
    userOrganization,
    userOrganization?.servicePlanId,
  ])

  useEffect(() => {
    let isUsersAndTeamsVisible = !isDummyOrg
    let orgAccessType = null

    if (isUsersAndTeamsVisible) {
      const orgUser = userOrganization?.users.find(
        (user) => user.id === currentUser?.id
      )

      orgAccessType = orgUser?.accessType

      if (orgAccessType === AccessTypes.Viewer) {
        isUsersAndTeamsVisible =
          userOrganization?.teams.some((team) =>
            team.users?.some(
              (teamUser) =>
                teamUser.id === currentUser?.id &&
                (teamUser.accessType === AccessTypes.Admin ||
                  teamUser.accessType === AccessTypes.Editor) &&
                teamUser.status === UserStatuses.Active
            )
          ) ?? false
      }
    }

    userOrganizationStore.dispatch({
      canAccessSettings: isUsersAndTeamsVisible,
      userOrgAccessType: orgAccessType,
    })
  }, [
    isDummyOrg,
    currentUser?.id,
    userOrganization?.teams,
    userOrganization?.users,
  ])

  useEffect(() => {
    if (locationState?.firstLogin && currentUser?.isEmailVerified === false) {
      addAlert({
        message: t('upgradePlan.verifySuccess'),
        severity: 'success',
      })
      window.history.replaceState({}, '')
    }
  }, [addAlert, currentUser?.isEmailVerified, locationState?.firstLogin, t])

  useEffect(() => {
    if (locationState?.emailVerified) {
      addAlert({
        message: (
          <Typography variant="body2">
            <Trans i18nKey="joinOrganization.thankYou" />
          </Typography>
        ),
        severity: 'success',
        timeout: 10000,
      })
      window.history.replaceState(
        {
          ...locationState,
          emailVerified: false,
        },
        ''
      )
    }
  }, [addAlert, locationState])

  useEffect(() => {
    handleClose()
  }, [location])

  useEffect(() => {
    if (!isAtsBullhorn) return

    const inIFrame = window.self !== window.top
    setDisplayOpenNewTab(inIFrame && state === ResumeBuilderStateEnum.NoFile)
  }, [isAtsBullhorn, state])

  return (
    <AppBar
      position="static"
      sx={{
        px: 4,
        background: theme.palette.gorillaResume?.dark,
        color: theme.palette.common.white,
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          mt: 2,
          pb: 2,
          px: '0px !important',
        }}
      >
        <Stack direction="row" gap={2}>
          <input
            type="image"
            onClick={navigateToParse}
            src="/assets/logos/logo-dark.svg"
            alt="GorillaResume"
            width={245}
            style={{
              cursor: 'pointer',
            }}
          />

          {displayOpenNewTab && (
            <Button
              startIcon={<OpenInNewRounded />}
              onClick={handleOpenNewTabClick}
              sx={{
                textTransform: 'none',
                color: 'white',
              }}
            >
              <Typography variant="body2Bold" color="white">
                {t('bullhorn.openNewTab')}
              </Typography>
            </Button>
          )}
        </Stack>

        <Stack direction="row" gap={4}>
          {servicePlan?.chargeType === ServicePlanChargeTypes.UsageCount && (
            <UsageStatus isATSBullhorn={isAtsBullhorn} />
          )}
          {isAtsBullhorn ? (
            <Stack direction="row" gap={2}>
              <Button
                variant="text"
                sx={{
                  textTransform: 'none',
                  color: 'white',
                }}
                startIcon={
                  <ErrorOutlineRounded sx={{ width: '25px', height: '25px' }} />
                }
                onClick={() => setIsReportProblemDialogOpen(true)}
              >
                {t('profileMenu.report')}
              </Button>
              <Button
                variant="text"
                sx={{
                  textTransform: 'none',
                  color: 'white',
                }}
                onClick={() => handleLanguageSwitch()}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <LanguageToggle isDarkMode />
                </Stack>
              </Button>
            </Stack>
          ) : (
            <>
              <Stack direction="row" gap={1} alignItems="center">
                <Avatar color={avatarColor} />
                <Stack sx={{ maxWidth: 200, textAlign: 'left' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      lineHeight: '24px',
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      fontSize: displayName === '' ? '14px' : '12px',
                    }}
                  >
                    {currentUser?.email ?? atsUser?.email}
                  </Typography>
                </Stack>
                <IconButton onClick={handleClick}>
                  <ExpandCircleDownOutlined sx={{ color: 'common.white' }} />
                </IconButton>
              </Stack>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                aria-hidden={!open}
                sx={{
                  '.MuiPaper-root': {
                    borderRadius: '16px',
                    p: 1.25,
                    boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                    maxWidth: 300,
                  },
                }}
                transformOrigin={{
                  vertical: -13,
                  horizontal: 'right',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <Stack direction="row" gap={1} p={1.75} alignItems="center">
                  <Avatar isSmall color={avatarColor} />
                  <Stack>
                    <Typography
                      variant="body2Bolder"
                      color="gorillaResume.text.primary"
                      lineHeight={1.5}
                    >
                      {displayName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="gorillaResume.text.secondary"
                      sx={{
                        fontSize: displayName === '' ? '14px' : '12px',
                      }}
                    >
                      {currentUser?.email ?? atsUser?.email}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider sx={{ mx: 1.75, mb: 1.75 }} />
                <Stack gap={1.25}>
                  <MenuItem
                    onClick={navigateToParse}
                    sx={MenuItemStyles}
                    disabled={(isDummyOrg && !isPendingJoin) || !servicePlan}
                  >
                    <FileOpenRounded />
                    <Typography variant="body2Bold">
                      {t('profileMenu.formatResume')}
                    </Typography>
                  </MenuItem>

                  {/* Re-enable when we implement the profile page */}
                  {/* <MenuItem onClick={handleClose} sx={MenuItemStyles}> */}
                  {/*  <PersonRounded /> */}
                  {/*  <Typography variant="body2Bold">Profile settings</Typography> */}
                  {/* </MenuItem> */}

                  {(!currentUser?.isEmailVerified ||
                    (!isDummyOrg && canAccessSettings)) && (
                    <Box
                      position="relative"
                      display="inline-block"
                      width="100%"
                    >
                      {!currentUser?.isEmailVerified && (
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          width="100%"
                          height="100%"
                          sx={{
                            background: 'rgba(229, 229, 229, 0.7)',
                            px: 2,
                            py: 1.25,
                            borderRadius: '8px',
                          }}
                          zIndex={1}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          gap={1.25}
                        >
                          <Typography
                            variant="body2Bold"
                            textAlign="center"
                            lineHeight="16.8px"
                          >
                            {t('profileMenu.verifyText')}
                          </Typography>
                          <RoundLoadingButton
                            variant="contained"
                            loading={isLoading}
                            onClick={handleSendVerificationEmail}
                            sx={{
                              backgroundColor: 'gorillaResume.button',
                              opacity: 1,
                            }}
                          >
                            {t('common.btn.verify')}
                          </RoundLoadingButton>
                        </Box>
                      )}
                      {(!currentUser?.isEmailVerified ||
                        (!isDummyOrg && canAccessSettings)) && (
                        <Stack position="relative" zIndex={0} gap={1.25}>
                          <MenuItem
                            disabled={
                              !currentUser?.isEmailVerified || !hasServicePlan
                            }
                            onClick={() => {
                              setAnchorEl(null)
                              navigate(CommonRoutesPaths.UsersAndTeams)
                            }}
                            sx={MenuItemStyles}
                          >
                            <GroupsRounded />
                            <Typography variant="body2Bold">
                              {teamManagementEnabled
                                ? t('profileMenu.usersAndTeams')
                                : t('profileMenu.usersWithoutTeams')}
                            </Typography>
                          </MenuItem>
                          <MenuItem
                            disabled={
                              !currentUser?.isEmailVerified ||
                              servicePlan?.chargeType !==
                                ServicePlanChargeTypes.PayAsYouGo
                            }
                            sx={MenuItemStyles}
                            onClick={() => {
                              setAnchorEl(null)
                              navigate(CommonRoutesPaths.Templates)
                            }}
                          >
                            <DocumentScannerRounded />
                            <Typography variant="body2Bold">
                              {t('profileMenu.templates')}
                            </Typography>
                          </MenuItem>

                          {((userOrgAccessType === AccessTypes.Admin &&
                            !isDummyOrg) ||
                            !currentUser?.isEmailVerified) && (
                            <MenuItem
                              disabled={
                                !currentUser?.isEmailVerified || !hasServicePlan
                              }
                              onClick={() => {
                                setAnchorEl(null)
                                navigate(CommonRoutesPaths.Organization)
                              }}
                              sx={MenuItemStyles}
                            >
                              <BusinessRounded />
                              <Typography variant="body2Bold">
                                {t('settings.organization.title')}
                              </Typography>
                            </MenuItem>
                          )}
                        </Stack>
                      )}
                    </Box>
                  )}

                  <MenuItem sx={MenuItemStyles} onClick={handleLanguageSwitch}>
                    <LanguageToggle />
                  </MenuItem>
                  <MenuItem
                    sx={MenuItemStyles}
                    onClick={() => setIsReportProblemDialogOpen(true)}
                    disabled={!currentUser?.isEmailVerified}
                  >
                    <ErrorOutlineRounded />
                    <Typography variant="body2Bold">
                      {t('profileMenu.report')}
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      authService.logout({
                        loginType: LoginTypes.GorillaResume,
                      })
                    }}
                    sx={{
                      ...MenuItemStyles,
                      color: 'gorillaResume.error.dark2',
                    }}
                  >
                    <LogoutRounded />
                    <Typography variant="body2Bold">
                      {t('profileMenu.logout')}
                    </Typography>
                  </MenuItem>
                </Stack>
              </Menu>
            </>
          )}
        </Stack>

        <ReportProblemDialog
          open={isReportProblemDialogOpen}
          onClose={() => setIsReportProblemDialogOpen(false)}
        />
      </Toolbar>
    </AppBar>
  )
}
